import type { DateTime } from 'effect';
import { Duration, Effect, Match, Option } from 'effect';

import { TOKEN_EXPIRATION_WINDOW_SECONDS } from '@vs-shop/engine/config/tokenRefresh';

import { DEFAULT_TIMEZONE } from '../../../config/clock';

import { checkTokenStatus, TokenStatus } from '@vs-shop/engine/auth/jwt';

import { reactivityKeys } from '@vs-shop/engine/atom/reactivityKeys';
import {
  mapResultOptionProperty,
  mapResultOptionToOption,
} from '@vs-shop/engine/atom/utils/result';
import { decodeAuthTokenFromCookies } from '../../flow/auth/decodeAuthToken';
import { RuntimeClientAtom } from '@vs-shop/engine/runtime/RuntimeClient';
import { AuthToken } from '../../service/auth/AuthToken';
import { getAuthTokenOption } from '@vs-shop/engine/utils/cookies/getAuthToken';
import * as Atom from 'effect/unstable/reactivity/Atom';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';

const authTokenAtom = RuntimeClientAtom.atom(getAuthTokenOption).pipe(
  Atom.withReactivity([reactivityKeys.authToken]),
  Atom.withServerValueInitial
);

export const authTokenValueAtom = mapResultOptionToOption(authTokenAtom);

const authTokenDecodedResultAtom = RuntimeClientAtom.atom(decodeAuthTokenFromCookies);

export const authTokenDecodedAtom = mapResultOptionToOption(authTokenDecodedResultAtom);

export const emailAtom = mapResultOptionProperty(authTokenDecodedResultAtom, 'username');

export const isLoggedInAtom = Atom.map(authTokenValueAtom, (token) => Option.isSome(token));

export type AuthSessionStatus = 'anonymous' | 'active' | 'needsRefresh' | 'sessionExpired';

const refreshTokenExpirationAtom = RuntimeClientAtom.atom(
  Effect.gen(function* () {
    const authTokenService = yield* AuthToken;
    return yield* authTokenService.getRefreshTokenExpiration.pipe(
      Effect.orElseSucceed(() => Option.none<number>())
    );
  })
).pipe(Atom.withReactivity([reactivityKeys.authToken]), Atom.withServerValueInitial);

export const authSessionStatusAtom = RuntimeClientAtom.atom(
  (get): Effect.Effect<AuthSessionStatus, never, DateTime.CurrentTimeZone> => {
    const tokenOption = get(authTokenValueAtom);

    const refreshExpirationOption = AsyncResult.match(get(refreshTokenExpirationAtom), {
      onInitial: () => Option.none<number>(),
      onFailure: () => Option.none<number>(),
      onSuccess: (s) => s.value,
    });

    return Effect.gen(function* () {
      const status = yield* checkTokenStatus(
        tokenOption,
        Duration.seconds(TOKEN_EXPIRATION_WINDOW_SECONDS),
        DEFAULT_TIMEZONE,
        refreshExpirationOption
      );

      return Match.value(status).pipe(
        Match.when(TokenStatus.noToken, () => 'anonymous' as const),
        Match.when(TokenStatus.ok, () => 'active' as const),
        Match.when(TokenStatus.isCloseToExpiration, () => 'needsRefresh' as const),
        Match.when(TokenStatus.isTokenExpired, () => 'needsRefresh' as const),
        Match.when(TokenStatus.isRefreshTokenExpired, () => 'sessionExpired' as const),
        Match.exhaustive
      );
    });
  }
);
